import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { Instrument, Watchlist, WatchlistDetail } from "@alpha/shared";
import { api, errorText } from "../api";
import { InstrumentSearch } from "../components/InstrumentSearch";
import { Empty, ErrorBox, Loading, PageHeader } from "../components/ui";
import { useMe } from "../me";
import { useAsync } from "../useAsync";

export function WatchlistsPage() {
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const lists = useAsync((s) => api.get<{ items: Watchlist[] }>("/v1/watchlists?take=100", s), []);
  const [name, setName] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const items = lists.data?.items ?? [];
  const selectedId = params.get("id") ?? items[0]?.id ?? null;

  async function create(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setBusy(true);
    try {
      const created = await api.post<Watchlist>("/v1/watchlists", { name });
      setName("");
      lists.reload();
      setParams({ id: created.id });
    } catch (err) {
      setFormError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <PageHeader title="Watchlists" />
      <div className="split">
        <div className="card narrow">
          <h3>Lists</h3>
          {lists.loading && <Loading />}
          {lists.error && <ErrorBox error={lists.error} onRetry={lists.reload} />}
          {!lists.loading && !lists.error && items.length === 0 && <Empty>No watchlists yet. Create one below.</Empty>}
          <ul className="plain-list">
            {items.map((w) => (
              <li key={w.id}>
                <button
                  type="button"
                  className={`list-btn ${w.id === selectedId ? "active" : ""}`}
                  onClick={() => setParams({ id: w.id })}
                >
                  <span>{w.name}</span>
                  <span className="muted">{w.itemCount}</span>
                </button>
              </li>
            ))}
          </ul>
          <form onSubmit={create} className="stack">
            <label>
              New watchlist
              <input value={name} maxLength={80} required onChange={(e) => setName(e.target.value)} placeholder="Name" />
            </label>
            <button type="submit" className="btn primary" disabled={busy || name.trim().length === 0}>
              {busy ? "Creating..." : "Create"}
            </button>
            {formError && (
              <p role="alert" className="error">
                {formError}
              </p>
            )}
          </form>
        </div>

        <div className="grow">
          {selectedId ? (
            <WatchlistView
              key={selectedId}
              id={selectedId}
              canEdit={(ownerId) => ownerId === me.id || me.role === "admin"}
              onDeleted={() => {
                setParams({});
                lists.reload();
              }}
              onChanged={lists.reload}
            />
          ) : (
            !lists.loading && <Empty>Select or create a watchlist.</Empty>
          )}
        </div>
      </div>
    </section>
  );
}

function WatchlistView({
  id,
  canEdit,
  onDeleted,
  onChanged,
}: {
  id: string;
  canEdit: (ownerId: string) => boolean;
  onDeleted: () => void;
  onChanged: () => void;
}) {
  const detail = useAsync((s) => api.get<WatchlistDetail>(`/v1/watchlists/${id}`, s), [id]);
  const [actionError, setActionError] = useState<string | null>(null);

  async function run(fn: () => Promise<unknown>, after: () => void) {
    setActionError(null);
    try {
      await fn();
      after();
    } catch (err) {
      setActionError(errorText(err));
    }
  }

  if (detail.loading) return <Loading />;
  if (detail.error || !detail.data) return <ErrorBox error={detail.error} onRetry={detail.reload} />;
  const w = detail.data;
  const editable = canEdit(w.ownerId);

  return (
    <div className="card">
      <div className="row between">
        <h3>{w.name}</h3>
        {editable && (
          <button
            type="button"
            className="btn small danger"
            onClick={() => {
              if (window.confirm(`Delete the watchlist "${w.name}"?`)) void run(() => api.del(`/v1/watchlists/${id}`), onDeleted);
            }}
          >
            Delete list
          </button>
        )}
      </div>
      {editable ? (
        <InstrumentSearch
          label="Add ticker"
          placeholder="Add a ticker"
          onSelect={(i: Instrument) =>
            void run(
              () => api.post(`/v1/watchlists/${id}/items`, { instrumentId: i.id }),
              () => {
                detail.reload();
                onChanged();
              },
            )
          }
        />
      ) : (
        <p className="muted">Owned by a colleague. You can view it but not change it.</p>
      )}
      {actionError && (
        <p role="alert" className="error">
          {actionError}
        </p>
      )}
      {w.items.length === 0 ? (
        <Empty>This watchlist is empty.</Empty>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Ticker</th>
              <th>Note</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {w.items.map((it) => (
              <tr key={it.id}>
                <td>
                  <Link to={`/chart?instrument=${it.instrumentId}`}>{it.ticker}</Link>
                </td>
                <td className="muted">{it.note ?? ""}</td>
                <td className="right">
                  {editable && (
                    <button
                      type="button"
                      className="btn small"
                      onClick={() =>
                        void run(
                          () => api.del(`/v1/watchlists/${id}/items/${it.id}`),
                          () => {
                            detail.reload();
                            onChanged();
                          },
                        )
                      }
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
